// ─────────────────────────────────────────────────────────────────────────────
// selection_matchers.ts — Shape → spell matchers for the selection family
//
// Each matcher is a pure function `(shape, ctx) → SpellMatch | null`.
// The matcher does the geometry-aware work (e.g. "find blocks enclosed
// by this lasso") so the recipe gets a ready-made parameter bag.
// ─────────────────────────────────────────────────────────────────────────────

import type { ShapeMatcher } from '../matcher';

interface SelectPointParams { blockId: string }
interface SelectRegionParams { blockIds: string[] }
interface SelectionEraseParams { strokeIds: string[] }

// ── select.point — dot on a block ───────────────────────────────────────────

export const selectPointMatcher: ShapeMatcher<SelectPointParams> = (shape, ctx) => {
  if (shape.kind !== 'dot') return null;
  const blockId = ctx.blockAtPoint(shape.center);
  if (!blockId) return null;
  return {
    spellId: 'select.point',
    params: { blockId },
  };
};

// ── select.region — any "drew around content" shape ───────────────────────
// Fires on both `closed` (lasso polygon) and `circle` (round loop) — for
// circles we synthesise a 24-vertex polygon so the Monte-Carlo
// containment test runs uniformly.
//
// Poka-yoke: returns null when no content is actually enclosed.  Empty
// regions never produce a selection — no orphan rectangle on the page.

const MIN_LASSO_AREA = 600;   // px² — drop accidental tiny loops
const CIRCLE_POLY_VERTS = 24;

function regionFromCircle(center: { x: number; y: number }, r: number): import('@shared/notebook_model').RegionBounds {
  const points: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < CIRCLE_POLY_VERTS; i++) {
    const a = (i / CIRCLE_POLY_VERTS) * Math.PI * 2;
    points.push({ x: center.x + Math.cos(a) * r, y: center.y + Math.sin(a) * r });
  }
  return {
    points,
    bbox: { x: center.x - r, y: center.y - r, width: 2 * r, height: 2 * r },
  };
}

export const selectRegionMatcher: ShapeMatcher<SelectRegionParams> = (shape, ctx) => {
  // ALWAYS fire when the user clearly drew a "select" gesture
  // (closed-ish shape with enough area). Even if no content lies
  // inside, we want the toast + console feedback to acknowledge
  // the detection. The recipe + reducer make sure no orphan
  // selection actually lands.
  let region: import('@shared/notebook_model').RegionBounds | null = null;
  if (shape.kind === 'closed') {
    const bb = shape.bounds.bbox;
    if (bb.width * bb.height < MIN_LASSO_AREA) return null;
    region = shape.bounds;
  } else if (shape.kind === 'circle') {
    if (Math.PI * shape.r * shape.r < MIN_LASSO_AREA) return null;
    region = regionFromCircle(shape.center, shape.r);
  } else {
    return null;
  }
  const blockIds = ctx.blocksInRegion(region);
  return {
    spellId: 'select.region',
    params: { blockIds },
  };
};

// ── selection.erase ─────────────────────────────────────────────────────────
// Trigger: a (preferably quick) straight line that crosses content.
// The matcher hands the recipe a list of stroke ids the line crossed —
// the recipe erases them and any owning ink block.
//
// Poka-yoke: returns null when the line crosses nothing. We never erase
// "the current selection just because there's one" — that needs a
// distinct, deliberate spell. Selection-aware erase comes later.

const ERASE_LINE_MIN_LENGTH = 12;
const ERASE_LINE_MIN_SPEED  = 0.2;   // px/ms — modest, "drawn with intent"

export const selectionEraseMatcher: ShapeMatcher<SelectionEraseParams> = (shape, ctx) => {
  if (shape.kind !== 'line') return null;
  if (shape.length < ERASE_LINE_MIN_LENGTH) return null;
  if (shape.speedPxPerMs < ERASE_LINE_MIN_SPEED) return null;
  // ALWAYS fire when the user drew a "slash" gesture — toast + console
  // surface the detection. Recipe handles the empty-crossings case
  // gracefully (no actions, but cast still resolves).
  const crossed = ctx.strokesCrossingLine(shape.from, shape.to);
  return {
    spellId: 'selection.erase',
    params: { strokeIds: crossed },
  };
};
